/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomFactoryCache, reatomResource } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui'

import { fetchUserProfileRequestAction } from './profile-requests'

import type { Network } from '@root/entities/network'
import type { DelugeId } from '@root/entities/user-short'

/**
 * Other user profile
 */
const reatomUserProfile_ = (options: {
  delugeId: DelugeId
  network: Network.Network
}) => {
  const stateAtom = reatomResource(
    async (ctx) => {
      const result = await ctx.schedule(() =>
        fetchUserProfileRequestAction(ctx, {
          network: options.network,
          delugeId: options.delugeId,
        }),
      )

      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
      }

      return result
    },
    {
      name: `reatomUserProfile_${options.delugeId}_${options.network}`,
      reconnectStrategy: 'refresh',
    },
  )

  return stateAtom
}

export const reatomUserProfile = reatomFactoryCache({
  factory: reatomUserProfile_,
  keyFn: ({ delugeId, network }) => {
    return `${network}.${delugeId}`
  },
})

export const otherUserDelugeIdAtom = atom<DelugeId | undefined>(
  undefined,
  'otherUserDelugeIdAtom',
)

export const userProfileModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const userDelugeId = ctx.spy(otherUserDelugeIdAtom)

  if (!userDelugeId) {
    return undefined
  }
  return reatomUserProfile({ delugeId: userDelugeId, network })
}, 'userProfileStateAtom')

export const userProfileStateAtom = atom((ctx) => {
  const model = ctx.spy(userProfileModelAtom)
  return model ? ctx.spy(model) : undefined
}, 'userProfileStateAtom')

export const userProfileAtom = atom(
  (ctx) => ctx.spy(userProfileStateAtom)?.value,
  'userProfileAtom',
)

export const refreshUserProfileAction = action((ctx, delugeId: DelugeId) => {
  const network = ctx.get(selectedNetworkAtom)
  const model = reatomUserProfile({ delugeId, network })

  return model.refresh(ctx)
}, 'refreshUserProfileAction')
